/**
 * Cliente HTTP. Siempre contra `/api` del mismo origen: en dev lo proxea Vite, en producción
 * Pages lo rutea al Worker. Una sola URL en el código y ninguna variable de entorno de front.
 */

const CLAVE_TOKEN = 'mg.token';

export class ErrorAPI extends Error {
  constructor(
    mensaje: string,
    readonly estado: number,
    readonly codigo?: string,
  ) {
    super(mensaje);
    this.name = 'ErrorAPI';
  }

  get esSesion(): boolean {
    return this.estado === 401;
  }
}

export function tokenGuardado(): string | null {
  try {
    return localStorage.getItem(CLAVE_TOKEN);
  } catch {
    return null;
  }
}

export function guardarToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(CLAVE_TOKEN, token);
    else localStorage.removeItem(CLAVE_TOKEN);
  } catch {
    // Sin localStorage la sesión vale mientras la pestaña esté abierta (queda la cookie).
  }
}

interface Opciones {
  metodo?: string;
  cuerpo?: unknown;
  /** Corta la espera: en un gimnasio con mala señal, un fetch puede quedar colgado. */
  timeoutMs?: number;
}

export async function pedir<T>(ruta: string, opciones: Opciones = {}): Promise<T> {
  const { metodo = 'GET', cuerpo, timeoutMs = 20_000 } = opciones;

  const cabeceras: Record<string, string> = {};
  if (cuerpo !== undefined) cabeceras['content-type'] = 'application/json';
  const token = tokenGuardado();
  if (token) cabeceras['authorization'] = `Bearer ${token}`;

  const abortador = new AbortController();
  const reloj = setTimeout(() => abortador.abort(), timeoutMs);

  let res: Response;
  try {
    res = await fetch(`/api${ruta}`, {
      method: metodo,
      headers: cabeceras,
      credentials: 'same-origin',
      signal: abortador.signal,
      ...(cuerpo === undefined ? {} : { body: JSON.stringify(cuerpo) }),
    });
  } catch (e) {
    clearTimeout(reloj);
    if (e instanceof DOMException && e.name === 'AbortError') {
      throw new ErrorAPI('La conexión tardó demasiado. Tus datos están guardados en el teléfono.', 0, 'timeout');
    }
    throw new ErrorAPI('Sin conexión. Lo que registres queda guardado y sube después.', 0, 'sin_red');
  } finally {
    clearTimeout(reloj);
  }

  const texto = await res.text();
  const datos = texto ? (JSON.parse(texto) as Record<string, unknown>) : {};

  if (!res.ok) {
    throw new ErrorAPI(
      typeof datos['error'] === 'string' ? datos['error'] : `Error ${res.status}`,
      res.status,
      typeof datos['codigo'] === 'string' ? datos['codigo'] : undefined,
    );
  }

  return datos as T;
}

export const estaEnLinea = (): boolean => (typeof navigator === 'undefined' ? true : navigator.onLine);
